//
// Copyright (c) 2011-2026 by Curalate, Inc.
//

import React, { useEffect } from 'react';
import { useFormContext } from 'react-hook-form';
import styled, { css } from 'styled-components';

import { ProductDetailsFieldLabels } from '@campaigns/components/shared/product-details-modal/drawer/constants/ProductDetailsModalFieldLabels';
import { ProductImageUpload } from '@campaigns/components/shared/product-details-modal/drawer/details/ProductImageUpload';
import { SAMPLING_CAMPAIGNS_TRACKING_ID } from '@campaigns/constants/sampling/SamplingCampaignsTrackingId';
import {
  SamplingProductValidationMessages,
  isProductImageContentTypeValid,
  isProductImageUrlValid,
} from '@campaigns/constants/sampling/SamplingProductValidationRulesConstants';
import { useProductModal } from '@campaigns/contexts/ProductModalContext';
import { EditableBVSamplingProduct } from '@campaigns/types/sampling/SamplingProductTypes';
import { PrimaryLinkButton } from '@components/atoms/button/Button';
import { ImageWithErrorHandling } from '@components/atoms/image-with-error-handling/ImageWithErrorHandling';
import { BodyText, CaptionText } from '@components/atoms/text/Text';
import { isDefined, isTruthy } from '@utils/filterPredicates';
import { SUPPORTED_IMAGE_EXTENSIONS } from '@utils/mimeTypes';
import { toGrammaticallyCorrectList } from '@utils/stringUtils';

interface ProductImageSectionProps {
  isDisabled?: boolean;
  isViewOnly?: boolean;
}

export const ProductImageSection = ({
  isDisabled = false,
  isViewOnly = false,
}: ProductImageSectionProps) => {
  const {
    clearErrors,
    formState: { errors },
    getValues,
    setError,
    setValue,
    watch,
  } = useFormContext<EditableBVSamplingProduct>();

  const {
    handleMainImageSelect,
    handleVariantImageSelect,
    isImageForAllVariants,
    mainProductImage,
    setShowImagePreview,
    showImagePreview,
  } = useProductModal();

  const imgUrl = watch('imgUrl');
  const imageError = errors.imgUrl?.message;
  const hasImage = isTruthy(imgUrl) && showImagePreview;

  // Revoke blob URLs owned by the form (imgUrl and variation imgUrl) on unmount,
  // since ProductModalContext only revokes its own preview URLs on close
  useEffect(() => {
    return () => {
      const currentValues = getValues();
      if (currentValues.imgUrl?.startsWith('blob:')) {
        URL.revokeObjectURL(currentValues.imgUrl);
      }
      currentValues.variations.forEach((variation) => {
        if (variation.imgUrl?.startsWith('blob:')) {
          URL.revokeObjectURL(variation.imgUrl);
        }
      });
    };
  }, [getValues]);

  const updateVariantImages = (newImageUrl: string) => {
    const currentValues = getValues();
    const updatedVariations = currentValues.variations.map((variation) => ({
      ...variation,
      imgUrl: newImageUrl,
    }));
    setValue('variations', updatedVariations, {
      shouldValidate: true,
    });
  };

  const clearExistingImage = () => {
    const currentImgUrl = getValues('imgUrl');

    if (currentImgUrl?.startsWith('blob:')) {
      URL.revokeObjectURL(currentImgUrl);
    }

    setValue('imgUrl', '');
  };

  const handleFileSelect = (file: File | undefined) => {
    clearExistingImage();

    if (file) {
      const blobUrl = URL.createObjectURL(file);
      setValue('imgUrl', blobUrl, {
        shouldValidate: false,
      });
      clearErrors('imgUrl');
      setShowImagePreview(true);

      if (isImageForAllVariants) {
        updateVariantImages(blobUrl);
      }
    } else {
      setValue('imgUrl', '', {
        shouldValidate: false,
      });
    }
    handleMainImageSelect(file);
  };

  const handlePreview = async () => {
    const currentUrl = watch('imgUrl');
    if (!currentUrl) {
      return;
    }
    if (!isProductImageUrlValid(currentUrl)) {
      setError('imgUrl', {
        message: SamplingProductValidationMessages.InvalidUrl,
      });
      return;
    }
    const isValid = await isProductImageContentTypeValid(currentUrl);
    // Guard against the URL having changed while the check was pending
    if (getValues('imgUrl') !== currentUrl) {
      return;
    }
    if (isValid) {
      setShowImagePreview(true);
    } else {
      setError('imgUrl', {
        message: SamplingProductValidationMessages.ImageUnsupported,
      });
    }
  };

  const handleToggleInputMode = () => {
    setValue('imgUrl', '', { shouldValidate: false });
    setShowImagePreview(false);
    handleMainImageSelect(undefined);
  };

  const handleClearImage = () => {
    clearErrors('imgUrl');
    const currentValues = getValues();
    const currentImgUrl = currentValues.imgUrl;

    // If we have a main image file and variants might be using it we should clone the file for each variant
    if (mainProductImage?.file && currentImgUrl?.startsWith('blob:')) {
      const variantIndices = currentValues.variations
        .map((variation, index) =>
          variation.imgUrl === currentImgUrl ? index : undefined,
        )
        .filter(isDefined);

      if (variantIndices.length > 0) {
        const updatedVariations = [...currentValues.variations];

        for (const index of variantIndices) {
          const fileClone = new File(
            [mainProductImage.file],
            mainProductImage.file.name,
            {
              type: mainProductImage.file.type,
            },
          );

          handleVariantImageSelect(index, fileClone);

          // Create a new blob URL for the preview
          const newBlobUrl = URL.createObjectURL(fileClone);

          updatedVariations[index] = {
            ...updatedVariations[index],
            imgUrl: newBlobUrl,
          };
        }

        setValue('variations', updatedVariations, {
          shouldValidate: true,
        });
      }
    }

    clearExistingImage();
    setShowImagePreview(false);
    handleMainImageSelect(undefined);
  };

  return (
    <Container>
      {hasImage ? (
        <>
          <PreviewHeader>
            <BodyText>{ProductDetailsFieldLabels.imgUrl}</BodyText>
          </PreviewHeader>
          <PreviewContainer>
            <PreviewImage alt='Product preview' src={imgUrl} />
            {!isViewOnly && (
              <PrimaryLinkButton
                onClick={handleClearImage}
                trackingId={
                  SAMPLING_CAMPAIGNS_TRACKING_ID.PRODUCT_MODAL_REMOVE_IMAGE_BUTTON
                }
              >
                Remove image
              </PrimaryLinkButton>
            )}
          </PreviewContainer>
        </>
      ) : (
        <>
          <PreviewHeader>
            <BodyText>{ProductDetailsFieldLabels.imgUrl}</BodyText>
          </PreviewHeader>
          {!isViewOnly && (
            <Instructions>
              File types supported:{' '}
              {toGrammaticallyCorrectList(SUPPORTED_IMAGE_EXTENSIONS)}
            </Instructions>
          )}
          <ProductImageUpload
            error={imageError}
            isDisabled={isDisabled}
            isViewOnly={isViewOnly}
            onClearImage={handleClearImage}
            onFileSelect={handleFileSelect}
            onPreview={handlePreview}
            onToggleInputMode={handleToggleInputMode}
            updateVariantImages={updateVariantImages}
          />
        </>
      )}
    </Container>
  );
};

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacings[1]};
`;

const Instructions = styled(CaptionText)(
  ({ theme }) => `
  color: ${theme.echo.color.text.onLight.subtle};
`,
);

const PreviewHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const PreviewImage = styled(ImageWithErrorHandling)`
  ${({ theme }) => css`
    width: ${theme.smallMeasure};
    height: ${theme.smallMeasure};
    border: 1px solid ${theme.colors.gray2};
    border-radius: ${theme.borderRadii[2]};
    object-fit: contain;
  `}
`;

const PreviewContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: ${({ theme }) => theme.spacings[1]};
`;
